import React from "react";

import LoadingSpinner from "@/components/common/LoadingSpinner";
import { ERROR_CODES } from "@/components/features/Login/error-codes";
import { Login } from "@/components/features/Login/user-login";
import {
  authQuery,
  describePasskeysQuery,
  finishRegisterPasskeyMutation,
  loginFinishMutation,
  requestLoginTokenMutation,
  selfQuery,
  startRegisterPasskeyMutation,
} from "@/lib/queries/auth";
import { organizationQuery } from "@/lib/queries/organization";
import { getQueryClient } from "@/lib/queries/queryClient";
import { parseJwt } from "@/lib/string/parsers";
import { captureException } from "@/services/sentry";
import type * as Models from "@kohost/api-client/models";
import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import type { QueryClient } from "@tanstack/react-query";
import type { LoaderFunction } from "react-router-dom";
import { redirect } from "react-router-dom";

const { VITE_AUTH_URL } = import.meta.env;

async function validateToken(
  queryClient: QueryClient,
  loginToken: string | null,
): Promise<boolean | Error> {
  try {
    const webAuthnSupported = browserSupportsWebAuthn();
    const { mutationFn: loginFinish } = loginFinishMutation(queryClient);
    const parsed = parseJwt(loginToken);

    await loginFinish({
      data: { ...parsed, token: loginToken },
    });

    const passkeys = webAuthnSupported
      ? await queryClient.ensureQueryData(describePasskeysQuery(queryClient))
      : [];

    if (webAuthnSupported && Array.isArray(passkeys) && passkeys.length === 0) {
      try {
        const { mutationFn: startRegisterPasskey } =
          startRegisterPasskeyMutation(queryClient);
        const { mutationFn: finishRegisterPasskey } =
          finishRegisterPasskeyMutation(queryClient);

        const { challengeId, challengeResponse } = await startRegisterPasskey();
        await finishRegisterPasskey({
          data: { challengeId, challengeResponse },
        });
      } catch (error) {
        captureException(error);
      }
    }

    return true;
  } catch (error) {
    if (error instanceof Error) {
      return error;
    }
    captureException(
      new Error("Unknown token validation error", { cause: error }),
    );
    return new Error("Unknown error");
  }
}

async function digitalKeyLogin(
  queryClient: QueryClient,
  loginToken: string | null,
  currentAuth: any,
) {
  const protocol = "mykey://";
  if (loginToken) {
    const mykeyUrl = new URL(protocol);
    mykeyUrl.searchParams.set("token", loginToken);
    window.open(mykeyUrl.toString(), "_self");
  } else if (currentAuth) {
    const { mutationFn: requestLoginToken } =
      requestLoginTokenMutation(queryClient);
    console.log("getting login token", currentAuth);
    const { token } = await requestLoginToken({
      data: {},
      headers: { "X-Organization-Id": currentAuth.organizationId },
    });

    const mykeyUrl = new URL(protocol);
    mykeyUrl.searchParams.set("token", token);
    mykeyUrl.searchParams.set("organizationId", currentAuth.organizationId);

    try {
      window.open(mykeyUrl.toString(), "_self");
    } catch (error) {
      captureException(error);
      const finalRedirectUri = new URL(VITE_AUTH_URL);
      finalRedirectUri.searchParams.set("redirect_uri", "mykey://");
      finalRedirectUri.searchParams.set(
        "organization_id",
        currentAuth.organizationId,
      );
      window.open(finalRedirectUri.toString(), "_self");
    }
  }

  return true;
}

interface LoginLoaderResult {
  organization: Promise<Models.Organization>;
  auth: Promise<any>;
  user: Promise<Models.User>;
  tokenValidation: boolean | Error | null;
}

const ErrorCodeKeys = Object.keys(ERROR_CODES).reduce(
  (acc, key) => {
    acc[key] = key as keyof typeof ERROR_CODES;
    return acc;
  },
  {} as Record<keyof typeof ERROR_CODES, keyof typeof ERROR_CODES>,
);

export const loader = (): LoaderFunction =>
  async function loader({ request }): Promise<LoginLoaderResult | Response> {
    const queryClient = getQueryClient("app");
    const searchParams = new URL(request.url).searchParams;
    const loginToken = searchParams.get("token");

    const intent = searchParams.get("intent");
    const redirectUri = new URL(
      searchParams.get("redirect_uri") || window.location.origin,
    ).href;
    const orgId = searchParams.get("organization_id");

    const authPromise = await queryClient
      .ensureQueryData(authQuery())
      .catch((e) => e);

    const loggedInOrgId = authPromise?.organizationId;

    const orgPromise = queryClient
      .ensureQueryData(
        organizationQuery(orgId ? orgId : loggedInOrgId ? loggedInOrgId : null),
      )
      .catch((e) => e);

    const selfPromise = queryClient
      .ensureQueryData(selfQuery())
      .catch((e) => e);
    const tokenValidationPromise =
      intent === "digitalKey"
        ? digitalKeyLogin(queryClient, loginToken, authPromise)
        : loginToken
          ? validateToken(queryClient, loginToken)
          : null;

    const authUrl = new URL(VITE_AUTH_URL);
    const currentUrl = new URL(window.location.href);

    const [org, auth, user, tokenValidation] = await Promise.all([
      orgPromise,
      authPromise,
      selfPromise,
      tokenValidationPromise,
    ]);

    if (
      org instanceof Error &&
      org.name === "NotFoundError" &&
      !searchParams.has("error_code")
    ) {
      const url = new URL(authUrl);
      url.searchParams.delete("organization_id");
      url.searchParams.delete("token");
      url.searchParams.delete("intent");
      url.searchParams.delete("redirect_uri");
      url.searchParams.set("error_code", ErrorCodeKeys.ORGANIZATION_NOT_FOUND);

      return redirect(url.toString());
    }

    const isAuthUrl =
      authUrl.origin === currentUrl.origin &&
      authUrl.pathname === currentUrl.pathname;

    if (!isAuthUrl) {
      const redirectUrl = new URL(redirectUri);
      const url = new URL(authUrl);
      url.searchParams.set("redirect_uri", redirectUrl.toString());
      if (orgId) {
        url.searchParams.set("organization_id", orgId);
      }
      if (currentUrl.searchParams.has("token")) {
        url.searchParams.set("token", currentUrl.searchParams.get("token")!);
      }

      return redirect(url.toString());
    }

    if (tokenValidation instanceof Error || tokenValidation === false) {
      searchParams.delete("token");
      searchParams.set("error_code", ErrorCodeKeys.SESSION_EXPIRED);
      const url = new URL(window.location.href);
      url.search = searchParams.toString();
      return redirect(url.toString());
    }

    if (
      tokenValidation === true &&
      redirectUri &&
      !redirectUri.includes("mykey://")
    ) {
      return redirect(redirectUri);
    }

    if (org && org.id && !searchParams.has("organization_id")) {
      searchParams.set("organization_id", org.id);
      const url = new URL(window.location.href);
      url.search = searchParams.toString();
      if (!searchParams.has("redirect_uri")) {
        const orgHostname = org.hostname;
        const redirectUri = `https://${orgHostname}`;
        url.searchParams.set("redirect_uri", new URL(redirectUri).toString());
      }
      return redirect(url.toString());
    }

    if (!searchParams.has("redirect_uri") && !searchParams.has("error_code")) {
      if (org && org.id && org.hostname) {
        searchParams.set("redirect_uri", `https://${org.hostname}`);
      } else {
        searchParams.set("redirect_uri", window.location.origin);
      }
      const url = new URL(window.location.href);
      url.search = searchParams.toString();
      return redirect(url.toString());
    }

    return {
      organization: org,
      auth: auth,
      user: user,
      tokenValidation: tokenValidation,
    };
  };

const LoginSuspense = ({ children }) => {
  return (
    <React.Suspense
      fallback={
        <div className="flex h-screen flex-col items-center justify-center">
          <div className="flex flex-col items-center space-y-4 rounded-lg p-8 shadow-sm">
            <LoadingSpinner width={32} height={32} fullScreen={false} />
            <div className="text-center">
              <h2 className="text-gray-900 text-lg font-medium">
                Logging you in
              </h2>
              <p className="0 mt-1 text-sm text-muted">
                Please wait a moment...
              </p>
            </div>
          </div>
          z
        </div>
      }
    >
      {children}
    </React.Suspense>
  );
};

export const LoginRoute = () => {
  return (
    <LoginSuspense>
      <Login />
    </LoginSuspense>
  );
};
